/**
 * Neue App-Version erkennen (F-47): die eigene Kennung steht als Meta-Tag in
 * der ausgelieferten index.html (siehe config/versionsKennung.ts), die
 * aktuelle auf dem Server in version.json. Gefragt wird beim Start, bei
 * Rueckkehr in die App und alle zehn Minuten, solange sie sichtbar ist.
 *
 * Im Entwicklungsserver und in den Tests fehlt das Meta-Tag - dann wird
 * nichts abgefragt. Neu geladen wird nie von selbst: das kostete z. B. einen
 * laufenden Pausentimer oder halb getippten Text.
 */
import { istNeueVersion, kennungAusDatei, pruefungFaellig } from '@/domain/appVersion';

export const META_NAME = 'ole-privat-version';
/** Bei sichtbarer App. */
export const INTERVALL_MS = 10 * 60_000;
/** Tab-Wechsel auf dem Chromebook nicht in Abrufe uebersetzen. */
export const MIN_ABSTAND_MS = 60_000;

export function eigeneVersion(doc: Document = document): string | null {
  return doc.querySelector<HTMLMetaElement>(`meta[name="${META_NAME}"]`)?.content || null;
}

/**
 * Kennung auf dem Server - `null` bei jedem Problem, ohne Meldung: offline,
 * abgelaufener Access-Login (Umleitung) oder die Ersatzseite von Cloudflare
 * Pages, die fuer fehlende Dateien index.html mit Status 200 liefert.
 */
export async function serverVersion(holen: typeof fetch = fetch, seite = window.location.href, jetzt = Date.now()): Promise<string | null> {
  try {
    // Relativ zur Seite, damit es auch unter einem Unterpfad (VITE_BASE) stimmt.
    const url = new URL('version.json', seite);
    url.searchParams.set('t', String(jetzt));
    const r = await holen(url, { cache: 'no-store', credentials: 'same-origin', redirect: 'manual' });
    if (r.type === 'opaqueredirect' || !r.ok) return null;
    if (!(r.headers.get('content-type') ?? '').includes('json')) return null;
    return kennungAusDatei(await r.json());
  } catch {
    return null;
  }
}

/** Beobachtet die Server-Version; meldet jede abweichende. Gibt `stop` zurueck. */
export function versionBeobachten(o: {
  eigene: string;
  beiNeu: (server: string) => void;
  holen?: () => Promise<string | null>;
  jetzt?: () => number;
}): () => void {
  const holen = o.holen ?? (() => serverVersion());
  const jetzt = o.jetzt ?? (() => Date.now());
  let aktiv = true;
  let zuletzt: number | null = null;

  async function pruefen() {
    if (!aktiv || document.visibilityState !== 'visible') return;
    if (!pruefungFaellig(jetzt(), zuletzt, MIN_ABSTAND_MS)) return;
    zuletzt = jetzt();
    const server = await holen();
    if (aktiv && server !== null && istNeueVersion(o.eigene, server)) o.beiNeu(server);
  }
  const ausloeser = () => void pruefen();

  document.addEventListener('visibilitychange', ausloeser);
  window.addEventListener('focus', ausloeser);
  window.addEventListener('online', ausloeser);
  const takt = setInterval(ausloeser, INTERVALL_MS);
  ausloeser();

  return () => {
    aktiv = false;
    document.removeEventListener('visibilitychange', ausloeser);
    window.removeEventListener('focus', ausloeser);
    window.removeEventListener('online', ausloeser);
    clearInterval(takt);
  };
}
